import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCheckCircle,
  faExclamationCircle,
  faTimesCircle,
  faBrain,
  faTable,
} from '@fortawesome/free-solid-svg-icons';

const CrossAssetMock = () => (
  <div className="space-y-4">
    <div className="rounded-lg overflow-hidden shadow-2xl shadow-black/30 border border-white/[0.06] font-mono" style={{ background: '#0f1923' }}>
      <div className="p-3 space-y-2">
        {/* CrossAssetBehaviourPanel mock */}
        <div className="bg-[#181825] rounded-lg border border-white/[0.06] p-4">
          {/* Header */}
          <div className="flex items-center justify-between mb-3 pb-2 border-b border-gray-700/50">
            <div className="flex items-center gap-2">
              <FontAwesomeIcon icon={faBrain} className="w-4 h-4 text-gray-400" />
              <div className="flex flex-col">
                <h3 className="text-[11px] font-medium text-gray-300 uppercase tracking-wide">
                  Cross-Asset Behaviour
                </h3>
                <span className="text-[9px] text-gray-500">CV: 14% (Low variance)</span>
              </div>
            </div>
            <div className="flex items-center gap-1.5 text-xs text-gray-300">
              <FontAwesomeIcon icon={faCheckCircle} className="w-3 h-3" />
              <span>Highly Consistent</span>
            </div>
          </div>

          {/* Narrative */}
          <p className="text-[10px] text-gray-400 mb-3">Consistent cross-asset behaviour with no single-market dependence.</p>

          {/* Return Distribution bar chart */}
          <div className="mb-3">
            <div className="text-[9px] text-gray-500 uppercase tracking-wide font-medium mb-1.5">Return Distribution</div>
            <div className="flex items-end gap-[3px] h-14">
              {[
                { asset: 'BTC', ret: 18 },
                { asset: 'ETH', ret: 14 },
                { asset: 'SOL', ret: 11 },
                { asset: 'BNB', ret: 7 },
                { asset: 'ADA', ret: 4 },
                { asset: 'DOGE', ret: -3 },
              ].map((d) => (
                <div key={d.asset} className="flex-1 flex flex-col items-center gap-0.5">
                  <div
                    className={`w-full rounded-t-sm ${d.ret >= 0 ? 'bg-cyan-400/55' : 'bg-red-400/60'}`}
                    style={{ height: `${Math.abs(d.ret) * 3.5}px`, minHeight: '4px' }}
                  />
                  <span className="text-[7px] text-gray-500">{d.asset}</span>
                </div>
              ))}
            </div>
            <div className="border-t border-dashed border-gray-600/40 mt-0.5 relative">
              <span className="absolute right-0 -top-2.5 text-[7px] text-gray-500">--- median</span>
            </div>
          </div>

          {/* Win Rate Consistency */}
          <div className="mb-3">
            <div className="text-[9px] text-gray-500 uppercase tracking-wide font-medium mb-1">Win Rate Consistency</div>
            <div className="flex gap-[2px] h-3">
              {[0.58, 0.55, 0.54, 0.52, 0.49, 0.47].map((wr, i) => (
                <div
                  key={i}
                  className="flex-1 rounded-sm bg-gray-400/70"
                  style={{ opacity: Math.abs(wr - 0.525) < 0.05 ? 1 : 0.5 }}
                />
              ))}
            </div>
          </div>

          {/* Behaviour Drift gauge */}
          <div className="mb-2">
            <div className="text-[9px] text-gray-500 uppercase tracking-wide font-medium mb-1">Behaviour Drift</div>
            <div className="h-1.5 bg-gray-700 rounded-full overflow-hidden">
              <div className="h-full rounded-full bg-cyan-500/80" style={{ width: '18%' }} />
            </div>
          </div>

          {/* Diagnostic metrics */}
          <div className="grid grid-cols-3 gap-2 mt-3 pt-2 border-t border-gray-700/50">
            <div>
              <div className="text-[8px] text-gray-500 uppercase tracking-wide">Edge Distribution</div>
              <div className="text-[10px] font-medium text-gray-300">Well Distributed</div>
            </div>
            <div>
              <div className="text-[8px] text-gray-500 uppercase tracking-wide">Drift</div>
              <div className="text-[10px] font-medium text-gray-300">Low</div>
            </div>
            <div>
              <div className="text-[8px] text-gray-500 uppercase tracking-wide">Regime</div>
              <div className="text-[10px] font-medium text-gray-300">Stable</div>
            </div>
          </div>

          <p className="text-[9px] text-gray-500 italic mt-2">Stable behaviour supports parameter robustness.</p>
        </div>

        {/* CrossAssetTableCard mock */}
        <div className="bg-[#181825] rounded-lg border border-white/[0.06] p-3">
          <div className="flex items-center gap-2 mb-3">
            <FontAwesomeIcon icon={faTable} className="w-3.5 h-3.5 text-gray-400" />
            <h3 className="text-[11px] font-medium text-gray-300 uppercase tracking-wide">
              Cross-Asset Performance
            </h3>
          </div>
          <table className="w-full text-[10px]">
            <thead>
              <tr className="text-[8px] text-gray-500 uppercase tracking-wide border-b border-gray-800">
                <th className="text-left pb-1.5">Asset</th>
                <th className="text-right pb-1.5">Return</th>
                <th className="text-right pb-1.5">Sharpe</th>
                <th className="text-right pb-1.5">Trades</th>
                <th className="text-right pb-1.5">Stability</th>
              </tr>
            </thead>
            <tbody>
              {[
                { asset: 'BTCUSDT', ret: 18.2, sharpe: 1.82, trades: 142, status: 'stable' as const },
                { asset: 'ETHUSDT', ret: 14.1, sharpe: 1.54, trades: 138, status: 'stable' as const },
                { asset: 'SOLUSDT', ret: 11.3, sharpe: 1.21, trades: 156, status: 'stable' as const },
                { asset: 'BNBUSDT', ret: 7.4, sharpe: 0.88, trades: 129, status: 'moderate' as const },
                { asset: 'ADAUSDT', ret: 4.1, sharpe: 0.62, trades: 134, status: 'moderate' as const },
                { asset: 'DOGEUSDT', ret: -2.8, sharpe: -0.14, trades: 118, status: 'weak' as const },
              ].map((r) => (
                <tr key={r.asset} className="border-b border-gray-800/50 last:border-0">
                  <td className="py-1.5 font-medium text-white">{r.asset.replace('USDT', '')}</td>
                  <td className={`py-1.5 text-right ${r.ret >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                    {r.ret >= 0 ? '+' : ''}{r.ret}%
                  </td>
                  <td className={`py-1.5 text-right ${r.sharpe >= 0.5 ? 'text-green-400' : r.sharpe >= 0 ? 'text-gray-300' : 'text-red-400'}`}>
                    {r.sharpe.toFixed(2)}
                  </td>
                  <td className="py-1.5 text-right text-gray-300">{r.trades}</td>
                  <td className="py-1.5 text-right">
                    <span className={`inline-flex items-center gap-0.5 ${r.status === 'stable' ? 'text-green-400' :
                      r.status === 'moderate' ? 'text-amber-400' : 'text-red-400'
                      }`}>
                      <FontAwesomeIcon
                        icon={r.status === 'stable' ? faCheckCircle : r.status === 'moderate' ? faExclamationCircle : faTimesCircle}
                        className="w-2.5 h-2.5"
                      />
                      <span className="text-[9px] capitalize">{r.status}</span>
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  </div>
);

export default CrossAssetMock;
